import { Refresh } from '@mui/icons-material';
import CloseIcon from '@mui/icons-material/Close';
import DevicesListFilter from 'components/DevicesFilter/DevicesListFilter.js';
// @mui material components
import { Box, Dialog, DialogContent, DialogTitle, Grid, IconButton, Stack, Typography } from '@mui/material';
import HeatMapDialogSelect from 'components/Dialog/HeatMapDialogSelect.js';
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import DashboardNavbar from 'examples/Navbars/DashboardNavbar';
import DashboardLayout from 'layouts/LayoutContainers/DashboardLayout';
import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchRangeDataOfMeasType, setHeatmapTimeRangeValue } from 'stores/sensor/heatmapDataSlice.js';
import { fetchCurrentUser, fetchUserDevices, setChartPopUp } from 'stores/sensor/sensorStore';
import DropdownMenuVertical from 'components/DropdownMenuVertical';
import { clearData } from 'stores/sensor/heatmapDataSlice.js';
import { setCurrentPage } from '../../stores/sensor/sensorStore';
import { Measurement } from 'types/measurement';
import { TimeInMS } from 'types/time';
import { brandColors } from 'types/colors';
import CustomSnackbar from '../../components/CustomSnackbar';
import FormattedChart from '../SensorPage/components/FormattedChart';
import HeatMap from './HeatMap.js';
import EmptyStateMessage from '../../components/MessageBox/EmptyStateMessage';
import Loading from '../../components/Loading/Loading';

export const Measurements = [
  'clean_air_level',
  'co2_ratio',
  'comfort_level',
  'co2_max',
  'rh',
  'temperature',
  'mass_pm_2_5',
  'mass_pm_10',
  // 'tvoc',
  'tvoc_max',
  // 'tvoc_level_min',
  'occupancy',
  'motion_counter',
];

const measurementsOpts = [
  { value: 'clean_air_level', name: 'Clean Air Level' },
  { value: 'co2_ratio', name: 'Ventilation Level' },
  { value: 'comfort_level', name: 'Comfort Level' },
  { value: 'co2_max', name: 'CO2' },
  { value: 'rh', name: 'RH' },
  { value: 'temperature', name: 'Temperature' },
  { value: 'mass_pm_2_5', name: 'PM 2.5' },
  { value: 'mass_pm_10', name: 'PM 10' },
  { value: 'tvoc_max', name: 'TVOC' },
  { value: 'occupancy', name: 'Occupancy' },
  { value: 'motion_counter', name: 'Motion' },
];

const buildingDemandOptions = [
  { value: 'occupancy', name: 'Occupancy', color: brandColors.BLACK },
  { value: 'thermal_justified', name: 'Thermal justified', color: brandColors.BLUE },
  { value: 'thermal_saveable', name: 'Thermal saveable', color: brandColors.GREEN },
  { value: 'thermal_requested', name: 'Thermal requested', color: brandColors.RED },
  { value: 'air_justified', name: 'Air justified', color: brandColors.BLUE },
  { value: 'air_saveable', name: 'Air saveable', color: brandColors.GREEN },
  { value: 'air_requested', name: 'Air requested', color: brandColors.RED },
];

const displayModeOptions = [
  { value: 'sensors', name: 'Sensors' },
  { value: 'levels', name: 'Levels' },
];

const chartFields = [
  Measurement.CAL,
  Measurement.Co2Ratio,
  Measurement.Co2,
  Measurement.RelativeHumidity,
  Measurement.Temperature,
  Measurement.PM2,
  Measurement.PM10,
  Measurement.TVOC,
  Measurement.COMFORT_LEVEL,
  Measurement.occupancy,
];

const CurrentDataMeasurments = [
  Measurement.CAL,
  Measurement.Co2,
  Measurement.Co2Ratio,
  Measurement.RelativeHumidity,
  Measurement.Temperature,
  Measurement.PM2,
  Measurement.PM10,
  Measurement.COMFORT_LEVEL,
  Measurement.TVOC,
  Measurement.occupancy,
];

/**
 * The refresh rate that symbolize that auto refresh is off
 */
const REFRESH_RATE_OFF = 0;

// Values in MS
const refreshRateOptions = [
  { label: 'Off', value: REFRESH_RATE_OFF },
  { label: '1 Minute', value: TimeInMS.Minute },
  { label: '5 Minutes', value: TimeInMS.Minute * 5 },
  { label: '10 Minutes', value: TimeInMS.Minute * 10 },
];

// last 12 months
const TimeRangeOptions = [
  // { label: "1 Minute", value: TimeInMS.Minute },
  { label: '1 Hour', value: TimeInMS.Hour },
  { label: 'Today', value: TimeInMS.Day },
  { label: '1 Week', value: TimeInMS.Week },
  { label: 'date to date', value: 'date to date' },
];

const REFRESH_TIME_IN_SECONDS = 300;
const dropdownScrollbarSx = {
  backgroundColor: brandColors.FILTER_BAR,
  scrollbarWidth: 'thin',
  scrollbarColor: `${brandColors.BLUE} ${brandColors.FILTER_BAR}`,
  '&::-webkit-scrollbar': {
    width: 8,
  },
  '&::-webkit-scrollbar-track': {
    backgroundColor: brandColors.FILTER_BAR,
  },
  '&::-webkit-scrollbar-thumb': {
    backgroundColor: brandColors.BLUE,
    borderRadius: 8,
    border: `2px solid ${brandColors.FILTER_BAR}`,
  },
};

function HeatMapPage() {
  const dispatch = useDispatch();
  const [isInitialized, setIsInitialized] = useState(false);

  const data = useSelector(state => state.heatmapData.data);
  const loading = useSelector(state => state.heatmapData.loading);
  const error = useSelector(state => state.heatmapData.error);
  const chartPopUp = useSelector(state => state.heatmapData.chartPopUp);
  const timeRangeObj = useSelector(state => state.heatmapData.timeRange);

  const currentUser = useSelector(state => state.sensors.currentUser);
  const filteredDevicesIds = useSelector(state => state.sensors.filteredDevicesIds);
  const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  // REUSE: Same filter state as DevicesList
  const { selectedFilterObject } = useSelector(state => state.persistence);
  const { distributor, customer, building, floor, area, status } = selectedFilterObject;
  const searchString = useSelector(state => state.persistence.searchString || '');

  const [snackbarState, setSnackbarState] = useState({
    snackbarOpen: false,
    vertical: 'top',
    horizontal: 'center',
  });

  const [measurementType, setMeasurementType] = useState(measurementsOpts[0].value);
  const [buildingDemandType, setBuildingDemandType] = useState('');
  const [displayMode, setDisplayMode] = useState(displayModeOptions[0].value);
  const selectedMetricType = buildingDemandType || measurementType;
  const { vertical, horizontal, snackbarOpen } = snackbarState;
  const rangeObj = useSelector(state => state.heatmapData.timeRange);

  // Initialization (as DevicesList)
  useEffect(() => {
    dispatch(setCurrentPage('heat-map'));
    const initializeData = async () => {
      dispatch(fetchCurrentUser());
      dispatch(fetchUserDevices({ devicesType: 'Sensor' }));
      setIsInitialized(true);
    };
    initializeData();

    return () => {
      dispatch(clearData());
    };
  }, [dispatch]);

  //  thunk handle validation
  const refreshHeatMapData = useCallback(() => {
    // Don't fetch if no building selected or no devices available
    if (!building || building === 'all' || filteredDevicesIds.length === 0) {
      return;
    }

    dispatch(
      fetchRangeDataOfMeasType({
        type: selectedMetricType,
        timeRange: timeRangeObj?.value || TimeInMS.Hour,
        filteredDevicesIds: filteredDevicesIds,
        userTimeZone: userTimeZone,
        displayMode,
      }),
    );
  }, [selectedMetricType, timeRangeObj, building, filteredDevicesIds, dispatch, userTimeZone, displayMode]);

  useEffect(() => {
    if (!isInitialized) return;

    refreshHeatMapData();
  }, [isInitialized, refreshHeatMapData]);

  const updateRangeData = () => {
    refreshHeatMapData();
  };

  const changeTimeRange = range => {
    dispatch(setHeatmapTimeRangeValue({ range }));
  };

  const handleSnackbarClose = () => {
    setSnackbarState({ ...snackbarState, snackbarOpen: false });
  };

  const handleSelectionOK = () => {
    dispatch(setHeatmapTimeRangeValue({ range }));
  };

  const sensorGroups =
    displayMode === 'sensors' && Array.isArray(data?.sensorsTypeMeasurements)
      ? Object.values(
          data.sensorsTypeMeasurements.reduce((groups, sensor) => {
            const floorNumber = sensor.floor_number;
            const areaName = sensor.area_name || 'Unassigned area';
            const key = `${floorNumber ?? 'unassigned'}-${areaName}`;

            if (!groups[key]) {
              groups[key] = {
                floorNumber,
                areaName,
                sensors: [],
              };
            }

            groups[key].sensors.push(sensor);
            return groups;
          }, {}),
        ).sort((a, b) => {
          const aFloor = Number(a.floorNumber);
          const bFloor = Number(b.floorNumber);
          const aHasFloor = Number.isFinite(aFloor);
          const bHasFloor = Number.isFinite(bFloor);

          if (aHasFloor && bHasFloor && aFloor !== bFloor) return aFloor - bFloor;
          if (aHasFloor !== bHasFloor) return aHasFloor ? -1 : 1;
          return String(a.areaName).localeCompare(String(b.areaName));
        })
      : [];
  // ApexCharts renders heatmap series bottom-to-top. Reverse the sorted list
  // so the visual order is lowest floor to highest floor from top to bottom.
  const sensorHeatmapRows = sensorGroups.flatMap(group => group.sensors).reverse();
  const hasSelectedFloor = floor !== null && floor !== undefined && floor !== '' && floor !== 'all' && floor !== 'undefined';
  const hasSelectedArea = area !== null && area !== undefined && area !== '' && area !== 'all' && area !== 'undefined';
  const levelHeatmapRows =
    displayMode === 'levels' && !hasSelectedFloor && !hasSelectedArea
      ? [...(data?.sensorsTypeMeasurements || [])].sort((a, b) => Number(b.level_id) - Number(a.level_id))
      : data?.sensorsTypeMeasurements || [];

  return (
    <DashboardLayout>
      <DashboardNavbar objectName="Heat map" />
      <Typography
        fontSize="11px"
        sx={{
          paddingLeft: { xs: 0, sm: '12px' },
          marginTop: { xs: '-15px', sm: '-5px' },
          textAlign: { xs: 'center', sm: 'left' },
        }}>
        Smart indoor air monitoring & management platform
      </Typography>
      <CustomSnackbar
        {...snackbarState}
        onClose={() => setSnack(s => ({ ...s, open: false }))}
        duration={4000}
        vertical="top"
        horizontal="left"
      />
      <Dialog open={chartPopUp !== undefined} fullWidth maxWidth="xl">
        <DialogTitle sx={{ padding: '0' }}>
          <IconButton aria-label="close" onClick={() => dispatch(setChartPopUp(undefined))}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ paddingBottom: '50px' }}>
          {chartPopUp ? (
            <FormattedChart
              title={chartPopUp?.title}
              datasets={chartPopUp?.datasets}
              isRange={chartPopUp?.isRange}
              setChartPopUp={setChartPopUp}
              isPopUp={true}
            />
          ) : undefined}
        </DialogContent>
      </Dialog>
      <MDBox pt={{ xs: 7, sm: 1 }} pb={1}>
        <Grid container spacing={4}>
          <Grid item xs={12}>
            <MDBox
              mb={1}
              px={2}
              variant="gradient"
              borderRadius="lg"
              coloredShadow="light"
              id="topBar"
              sx={{
                position: 'relative',
                zIndex: 1200,
              }}
              style={{
                background: '#DEE4F9',
              }}>
              <Box
                sx={{
                  display: { xs: 'flex', sm: 'grid' },
                  gridTemplateColumns: { sm: '1fr auto 1fr', lg: 'minmax(0, 1fr) minmax(0, 900px) minmax(0, 1fr)' },
                  gridTemplateRows: 'auto auto',
                  flexDirection: { xs: 'column', sm: 'row' },
                  alignItems: 'center',
                  gap: { xs: 1, sm: 0 },
                }}>
                <Stack
                  direction="row"
                  marginLeft={2}
                  spacing={2}
                  alignItems="end"
                  className="top-bar"
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gridColumn: 2,
                    gridRow: 1,
                    justifySelf: 'center',
                  }}>
                  <MDTypography variant="h4" color="dark">
                    Heat map - Dynamic IAQ Trends
                  </MDTypography>
                </Stack>

                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'row' },
                    alignItems: { xs: 'stretch', sm: 'center' },
                    justifyContent: 'center',
                    gap: 1,
                    width: { xs: '100%', sm: 'auto', lg: '100%' },
                    gridColumn: { sm: 2, xl: '1 / span 3' },
                    gridRow: 2,
                  }}>
                  <DevicesListFilter
                    status={false}
                    search={false}
                    hvacEquipment
                    extraFilterItemWidths={[118, 182, 182]}
                    lgTwoRowLayout
                    extraDropdownItems={[
                      <DropdownMenuVertical
                        key="display-mode"
                        labelName="Display"
                        options={displayModeOptions}
                        selectedOption={displayMode}
                        handleChange={e => setDisplayMode(e.target.value)}
                        width="100%"
                        menuScrollbarSx={dropdownScrollbarSx}
                        shrinkLabel
                      />,
                      <DropdownMenuVertical
                        key="measurement"
                        labelName="Measurement"
                        options={measurementsOpts}
                        selectedOption={measurementType}
                        handleChange={e => {
                          setMeasurementType(e.target.value);
                          setBuildingDemandType('');
                        }}
                        width="100%"
                        menuScrollbarSx={dropdownScrollbarSx}
                        shrinkLabel
                      />,
                      <DropdownMenuVertical
                        key="building-demand"
                        labelName="Building demand"
                        options={buildingDemandOptions}
                        selectedOption={buildingDemandType}
                        handleChange={e => {
                          setBuildingDemandType(e.target.value);
                          setMeasurementType('');
                        }}
                        width="100%"
                        menuScrollbarSx={dropdownScrollbarSx}
                        shrinkLabel
                      />,
                    ]}
                  />
                </Box>

                <Stack
                  direction="row"
                  spacing={1}
                  sx={{
                    alignItems: 'center',
                    justifyContent: 'flex-end',
                    gridColumn: 3,
                    gridRow: '1 / span 2',
                  }}>
                  <HeatMapDialogSelect
                    buttonTitle="Time Range"
                    buttonSubTitle={rangeObj?.label}
                    dialogTitle="Choose Time Range"
                    color="black"
                    onSelectionChanged={() => changeTimeRange()}
                    onSelectionOK={setHeatmapTimeRangeValue}
                    // onSelectionOK={handleSelectionOK}
                    selectLabel="Time Range"
                    selectOptions={TimeRangeOptions}
                    default={rangeObj}
                    toolTip="The time range is limited to 7 days from the selected start date to the end date."
                  />

                  {/* <IconButton
                    color="black"
                    // onClick={() => refreshHeatMapData()}
                    onClick={() => updateRangeData()}>
                    <Refresh />
                  </IconButton> */}
                </Stack>
              </Box>
            </MDBox>
          </Grid>
        </Grid>
      </MDBox>
      <MDBox
        container
        sx={{
          overflowX: 'visible',
          overflowY: 'visible',
          display: 'flex',
          height: '100%',
          flexDirection: {
            xs: 'column',
            lg: 'row',
          },
          justifyContent: 'space-between',
        }}
        className="bodycontainer">
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            marginTop: '-.5rem',
            flexGrow: '1',
          }}>
          {/* Updated condition to use filteredDevicesIds */}
          {building === 'all' ? (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                height: '50vh',
              }}>
              <EmptyStateMessage message="Please select a building to view the heat map" maxHeight="10rem" width="30rem" />
            </Box>
          ) : filteredDevicesIds.length === 0 ? (
            <Box
              className="no-sensors-msg"
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '50vh',
                opacity: 0,
                animation: 'fadeIn 0.4s ease 1s forwards',
                '@keyframes fadeIn': {
                  from: { opacity: 0 },
                  to: { opacity: 1 },
                },
              }}>
              <Typography
                sx={{
                  fontWeight: '500',
                  fontSize: '1.6rem',
                  textAlign: 'center',
                }}>
                No sensors found with current filters
              </Typography>
            </Box>
          ) : loading ? (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '50vh',
              }}>
              <Loading />
            </Box>
          ) : error ? (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '50vh',
              }}>
              <Typography
                sx={{
                  fontSize: '1.2rem',
                  textAlign: 'center',
                  color: 'error.main',
                }}>
                Error loading heat map: {error}
              </Typography>
            </Box>
          ) : (
            <>
              {(() => {
                if (Array.isArray(data?.sensorsTypeMeasurements) && data.sensorsTypeMeasurements.length > 0) {
                  if (displayMode === 'sensors') {
                    return (
                      <HeatMap
                        type={selectedMetricType}
                        timeRangeObj={rangeObj}
                        sensorsList={sensorHeatmapRows}
                        showLocationColumns
                        isBuildingDemand={Boolean(buildingDemandType)}
                      />
                    );
                  }

                  return (
                    <HeatMap
                      type={selectedMetricType}
                      timeRangeObj={rangeObj}
                      sensorsList={levelHeatmapRows}
                      isBuildingDemand={Boolean(buildingDemandType)}
                    />
                  );
                } else {
                  return (
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        alignItems: 'center',
                        height: '50vh',
                      }}>
                      <Typography
                        sx={{
                          fontSize: '1.2rem',
                          textAlign: 'center',
                          mb: 2,
                        }}>
                        No heat map data available for the selected filters
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: '0.9rem',
                          textAlign: 'center',
                          color: 'text.secondary',
                        }}></Typography>
                    </Box>
                  );
                }
              })()}
            </>
          )}
        </Box>
      </MDBox>

      {/* debug info to use filteredDevicesIds */}
      {/* <Box sx={{ 
        position: 'fixed', 
        bottom: 10, 
        right: 10, 
        fontSize: '10px', 
        background: 'rgba(0,0,0,0.7)', 
        color: 'white', 
        padding: '4px',
        borderRadius: '4px'
      }}>
        HeatMap: {filteredDevicesIds.length} devices | C:{customer} B:{building} F:{floor} A:{area}
      </Box> */}
    </DashboardLayout>
  );
}

export default HeatMapPage;
